import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Send, Sparkles, AtSign, Loader2 } from 'lucide-react';
import { useAuthInfo } from '@propelauth/react';
import { cn } from '../../../../../lib/utils';
import MarkdownRenderer from '../../../../../components/agno/MarkdownRenderer';
import useSignalBuilderStream from '../hooks/useSignalBuilderStream';
import DescriptionSelectorStep from './steps/DescriptionSelectorStep';
import EntitySelectorStep from './steps/EntitySelectorStep';
import TriggerSelectorStep from './steps/TriggerSelectorStep';
import FilterBuilderStep from './steps/FilterBuilderStep';
import DestinationSelectorStep from './steps/DestinationSelectorStep';
import SignalSummaryStep from './steps/SignalSummaryStep';
import SignalCreatedStep from './steps/SignalCreatedStep';
import SkillSelectorStep from './steps/SkillSelectorStep';
import SlackChannelSelectorStep from './steps/SlackChannelSelectorStep';
import { ChatMessageAvatar, ChatSenderLabel, getChatUserInitials } from '../../../../../shared/chat/ChatMessageIdentity';
import type { SignalBuilderMessage } from '../hooks/useSignalBuilderStream';

interface Turn {
  id: string;
  role: 'user' | 'assistant';
  parts: SignalBuilderMessage[];
}

function groupMessagesIntoTurns(messages: SignalBuilderMessage[]): Turn[] {
  const turns: Turn[] = [];
  let current: Turn | null = null;

  for (const msg of messages) {
    if (msg.role === 'user') {
      current = { id: msg.id, role: 'user', parts: [msg] };
      turns.push(current);
      current = null;
    } else {
      // assistant or tool — belongs to the current assistant turn
      if (!current || current.role !== 'assistant') {
        current = { id: msg.id, role: 'assistant', parts: [msg] };
        turns.push(current);
      } else {
        current.parts.push(msg);
      }
    }
  }
  return turns;
}

interface SignalChatBuilderProps {
  onBack?: () => void;
  showBackButton?: boolean;
  title?: string;
  subtitle?: string;
  activeSignalName?: string | null;
  externalPrompt?: string | null;
  onExternalPromptConsumed?: () => void;
  draftSignalConfig?: SignalBuilderDraftConfig | null;
  onDraftSignalConfigChange?: (draftSignalConfig: SignalBuilderDraftConfig | null) => void;
}

export interface SignalBuilderDraftConfig {
  [key: string]: unknown;
  name?: string;
  description?: string;
  trigger?: 'usage_based' | 'event_based';
  entity_relation?: string;
  filters?: any;
  metrics?: string[];
  sort?: {
    field?: string;
    order?: 'asc' | 'desc';
    type?: string;
  };
  selected_fields?: string[];
  automation_actions?: {
    actions?: Array<{
      type: string;
      assign_to?: string;
      assign_to_key?: string;
    }>;
    suppression_period?: string;
    run_schedule?: string;
    model_key?: string | null;
  };
  selected_skill_ids?: string[];
  selected_skills?: Array<{ id: string; name: string }>;
}

const STEP_COMPONENTS: Record<string, React.FC<any>> = {
  description_suggestions: DescriptionSelectorStep,
  entity_selector: EntitySelectorStep,
  trigger_selector: TriggerSelectorStep,
  filter_builder: FilterBuilderStep,
  destination_selector: DestinationSelectorStep,
  signal_summary: SignalSummaryStep,
  signal_created: SignalCreatedStep,
  skill_selector: SkillSelectorStep,
  slack_channel_selector: SlackChannelSelectorStep,
};

const DEFAULT_SELECTED_FIELDS: Record<'accounts' | 'users', string[]> = {
  accounts: ['accounts.name', 'accounts.last_seen', 'accounts.created_at'],
  users: ['users.name', 'users.last_seen', 'users.created_at'],
};

const parseFiltersFromSelection = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const SignalChatBuilder: React.FC<SignalChatBuilderProps> = ({
  onBack,
  showBackButton = true,
  title = 'Create Signal with ORA',
  subtitle = 'Follow the steps to build your signal',
  activeSignalName = null,
  externalPrompt = null,
  onExternalPromptConsumed,
  draftSignalConfig,
  onDraftSignalConfigChange,
}) => {
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [localDraftSignalConfig, setLocalDraftSignalConfig] = useState<SignalBuilderDraftConfig | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const consumedExternalPromptRef = useRef<string | null>(null);
  const lastProcessedToolMessageIdRef = useRef<string | null>(null);
  const authInfo: any = useAuthInfo();
  const userInitials = getChatUserInitials(authInfo?.user);

  const isDraftControlled = draftSignalConfig !== undefined;
  // In controlled mode, prefer local state (which commitDraftSignalConfig keeps in sync)
  // but fall back to parent prop. This ensures immediate UI updates even before the
  // parent re-renders with the new value from onDraftSignalConfigChange.
  const effectiveDraftSignalConfig = localDraftSignalConfig ?? (isDraftControlled ? draftSignalConfig : null);

  const commitDraftSignalConfig = useCallback((nextDraft: SignalBuilderDraftConfig | null) => {
    // Always update local state so UI reflects changes immediately
    setLocalDraftSignalConfig(nextDraft);
    if (onDraftSignalConfigChange) {
      onDraftSignalConfigChange(nextDraft);
    }
  }, [onDraftSignalConfigChange]);

  const { messages, isStreaming, isAssistantTyping, sendMessage, resetChat } = useSignalBuilderStream({
    draftSignalConfig: effectiveDraftSignalConfig,
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!isDraftControlled) return;
    setLocalDraftSignalConfig(draftSignalConfig ?? null);
  }, [draftSignalConfig, isDraftControlled]);

  useEffect(() => {
    const latestToolMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'tool' && msg.toolCall?.status === 'complete' && (msg.toolCall?.result?.ui_step || msg.toolCall?.tool_name === 'create_custom_skill_for_signal'));
    if (!latestToolMessage) return;
    if (lastProcessedToolMessageIdRef.current === latestToolMessage.id) return;

    lastProcessedToolMessageIdRef.current = latestToolMessage.id;
    const result = latestToolMessage.toolCall?.result || {};

    if (result.ui_step === 'filter_builder') {
      if (!result.suggested_filters && !result.entity_type) return;
      commitDraftSignalConfig({
        ...(effectiveDraftSignalConfig || {}),
        ...(result.entity_type ? { entity_relation: result.entity_type } : {}),
        ...(result.suggested_filters ? { filters: result.suggested_filters } : {}),
      });
      return;
    }

    if (result.ui_step === 'signal_summary' && result.signal_config) {
      // Preserve frontend-only fields that the backend doesn't know about
      const currentSkillIds = (effectiveDraftSignalConfig as any)?.selected_skill_ids;
      const currentSkills = (effectiveDraftSignalConfig as any)?.selected_skills;
      const editingId = (effectiveDraftSignalConfig as any)?.editing_signal_id;
      commitDraftSignalConfig({
        ...result.signal_config,
        ...(currentSkillIds?.length ? { selected_skill_ids: currentSkillIds } : {}),
        ...(currentSkills?.length ? { selected_skills: currentSkills } : {}),
        ...(editingId ? { editing_signal_id: editingId } : {}),
      });
      return;
    }

    // Track skills created via create_custom_skill_for_signal
    if (latestToolMessage.toolCall?.tool_name === 'create_custom_skill_for_signal' && result.skill_id && result.success) {
      commitDraftSignalConfig({
        ...(effectiveDraftSignalConfig || {}),
        selected_skill_ids: [result.skill_id],
        selected_skills: [{ id: result.skill_id, name: result.display_name || result.skill_name || 'Custom Skill' }],
      });
    }
  }, [messages, effectiveDraftSignalConfig, commitDraftSignalConfig]);

  useEffect(() => {
    if (!externalPrompt) {
      consumedExternalPromptRef.current = null;
    }
  }, [externalPrompt]);

  useEffect(() => {
    if (!externalPrompt || isStreaming) return;
    if (consumedExternalPromptRef.current === externalPrompt) return;

    consumedExternalPromptRef.current = externalPrompt;
    sendMessage(externalPrompt, {
      draftOverride: effectiveDraftSignalConfig ?? null,
    });
    if (onExternalPromptConsumed) onExternalPromptConsumed();
  }, [effectiveDraftSignalConfig, externalPrompt, isStreaming, onExternalPromptConsumed, sendMessage]);

  const handleSend = () => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleUseSelectedSignalContext = () => {
    if (!activeSignalName || isStreaming) return;
    sendMessage(`Let's update the signal "${activeSignalName}".`, {
      draftOverride: effectiveDraftSignalConfig ?? null,
    });
  };

  const handleStepSelect = useCallback((uiStep: string | undefined, value: string, label: string) => {
    const currentDraft = effectiveDraftSignalConfig || {};
    let nextDraft: SignalBuilderDraftConfig | null = null;
    let draftForTurn: SignalBuilderDraftConfig | null | undefined = undefined;

    if (uiStep === 'description_suggestions') {
      // Find the signal_name from the latest description_suggestions tool result
      const descToolMsg = [...(messages || [])].reverse().find(
        (msg) => msg.role === 'tool' && msg.toolCall?.result?.ui_step === 'description_suggestions',
      );
      const signalName = descToolMsg?.toolCall?.result?.signal_name || currentDraft.name;
      nextDraft = {
        ...currentDraft,
        ...(signalName ? { name: signalName } : {}),
        description: value,
      };
    } else if (uiStep === 'entity_selector') {
      const entity = value === 'users' ? 'users' : 'accounts';
      nextDraft = {
        ...currentDraft,
        entity_relation: entity,
        selected_fields: (currentDraft.selected_fields && currentDraft.selected_fields.length > 0)
          ? currentDraft.selected_fields
          : DEFAULT_SELECTED_FIELDS[entity],
        sort: currentDraft.sort?.field
          ? currentDraft.sort
          : {
              field: `${entity}.last_seen`,
              order: 'desc',
              type: 'property',
            },
      };
    } else if (uiStep === 'destination_selector') {
      const destinationValues = value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);

      nextDraft = {
        ...currentDraft,
        automation_actions: {
          suppression_period: currentDraft.automation_actions?.suppression_period || 'all_time',
          run_schedule: currentDraft.automation_actions?.run_schedule || '0 9 * * *',
          actions: destinationValues.map((destination) => ({
            type: destination,
            assign_to: 'owner',
            assign_to_key: 'owner',
          })),
        },
      };
    } else if (uiStep === 'filter_builder') {
      const parsedFilters = parseFiltersFromSelection(value);
      if (parsedFilters) {
        nextDraft = {
          ...currentDraft,
          filters: parsedFilters,
        };
      }
    } else if (uiStep === 'skill_selector') {
      // value is skill_id, 'create_new', or 'skip'
      if (value !== 'skip' && value !== 'create_new') {
        nextDraft = {
          ...currentDraft,
          selected_skill_ids: [value],
          selected_skills: [{ id: value, name: label }],
        };
      }
    }

    // Handle slack_channel_selector draft update (needs to be before the commit block)
    if (uiStep === 'slack_channel_selector') {
      const currentActions = currentDraft.automation_actions?.actions || [];
      const updatedActions = currentActions.map((a: any) =>
        a.type === 'send_to_slack' ? { ...a, channel_id: value } : a
      );
      nextDraft = {
        ...currentDraft,
        automation_actions: {
          ...currentDraft.automation_actions,
          actions: updatedActions,
        },
      };
    }

    if (nextDraft) {
      commitDraftSignalConfig(nextDraft);
      draftForTurn = nextDraft;
    }

    // Build a concise user-facing message based on step type
    let userMessage: string;
    if (uiStep === 'description_suggestions') {
      const truncated = label.length > 80 ? `${label.slice(0, 77)}...` : label;
      userMessage = `I'll go with this description: "${truncated}"`;
    } else if (uiStep === 'entity_selector') {
      userMessage = `Use **${label}** as the entity`;
    } else if (uiStep === 'trigger_selector') {
      userMessage = `Use **${label}** trigger`;
    } else if (uiStep === 'destination_selector') {
      userMessage = `Send to **${label}**`;
    } else if (uiStep === 'slack_channel_selector') {
      userMessage = `Send to Slack channel **#${label}**`;
    } else if (uiStep === 'skill_selector') {
      if (value === 'skip') {
        userMessage = 'No analysis needed';
      } else if (value === 'create_new') {
        userMessage = 'Create a new analysis for this signal';
      } else {
        userMessage = `Use **${label}** for the analysis`;
      }
    } else if (uiStep === 'signal_summary') {
      const summaryToolMsg = [...(messages || [])].reverse().find(
        (msg) => msg.role === 'tool' && msg.toolCall?.result?.ui_step === 'signal_summary',
      );
      const summaryDraft = summaryToolMsg?.toolCall?.result?.signal_config;
      if (summaryDraft) {
        // Preserve frontend-only fields when sending draft to backend
        const currentSkillIds = currentDraft.selected_skill_ids;
        const currentSkills = currentDraft.selected_skills;
        const editingId = currentDraft.editing_signal_id;
        const chosenModelKey = currentDraft.automation_actions?.model_key;
        draftForTurn = {
          ...summaryDraft,
          ...(currentSkillIds?.length ? { selected_skill_ids: currentSkillIds } : {}),
          ...(currentSkills?.length ? { selected_skills: currentSkills } : {}),
          ...(editingId ? { editing_signal_id: editingId } : {}),
          ...(chosenModelKey
            ? {
                automation_actions: {
                  ...(summaryDraft.automation_actions || {}),
                  model_key: chosenModelKey,
                },
              }
            : {}),
        };
      }
      userMessage = label;
    } else {
      userMessage = `Selected **${label}**`;
    }
    sendMessage(userMessage, draftForTurn !== undefined ? { draftOverride: draftForTurn } : undefined);
  }, [effectiveDraftSignalConfig, commitDraftSignalConfig, sendMessage, messages]);

  return (
    <div className="flex h-full min-h-0 bg-background">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 border-b border-border bg-background px-6 py-4">
          <div className="flex items-center gap-4">
            {showBackButton ? (
              <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : null}
            <div className="flex items-center gap-3">
              <div className="rounded-lg border border-border bg-muted p-2">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold text-foreground">{title}</h1>
                <p className="text-[13px] text-muted-foreground">{subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl space-y-6">
            {groupMessagesIntoTurns(messages).map((turn) => {
              if (turn.role === 'user') {
                const message = turn.parts[0];
                return (
                  <div key={turn.id} className="flex justify-end gap-3">
                    <div className="flex max-w-[75%] flex-col gap-1 items-end">
                      <ChatSenderLabel role="user" />
                      <div className="w-full rounded-lg rounded-tr-sm bg-accent-tint px-3.5 py-2.5 text-foreground">
                        <p className="whitespace-pre-wrap text-sm leading-[1.8] text-foreground">{message.content}</p>
                      </div>
                    </div>
                    <ChatMessageAvatar role="user" userInitials={userInitials} className="mt-0.5" />
                  </div>
                );
              }

              // Assistant turn — single avatar, multiple parts
              return (
                <div key={turn.id} className="flex justify-start gap-3">
                  <ChatMessageAvatar role="assistant" className="mt-0.5" />
                  <div className="flex flex-col gap-2 max-w-[75%]">
                    <ChatSenderLabel role="assistant" />
                    {turn.parts.map((part) => {
                      // Tool message
                      if (part.role === 'tool' && part.toolCall) {
                        const isRunning = part.toolCall.status === 'running';
                        const uiStep = part.toolCall.result?.ui_step;
                        const StepComponent = STEP_COMPONENTS[uiStep];

                        if (StepComponent) {
                          const extraProps: Record<string, unknown> = {};
                          if (uiStep === 'signal_created') {
                            extraProps.onCreateAnother = () => {
                              commitDraftSignalConfig(null);
                              resetChat();
                            };
                            extraProps.onGoBack = onBack;
                          }
                          if (uiStep === 'signal_summary') {
                            extraProps.modelKey =
                              effectiveDraftSignalConfig?.automation_actions?.model_key ??
                              part.toolCall.result?.signal_config?.automation_actions?.model_key ??
                              undefined;
                            extraProps.onModelKeyChange = (key: string) => {
                              const base = effectiveDraftSignalConfig || ({} as SignalBuilderDraftConfig);
                              commitDraftSignalConfig({
                                ...base,
                                automation_actions: {
                                  ...(base.automation_actions || {}),
                                  model_key: key,
                                },
                              });
                            };
                          }
                          return (
                            <StepComponent
                              key={part.id}
                              result={part.toolCall.result}
                              onSelect={(value: string, label: string) => {
                                handleStepSelect(uiStep, value, label);
                              }}
                              {...extraProps}
                            />
                          );
                        }

                        // Internal tools without ui_step — show loader while running, hide when complete
                        if (!isRunning) return null;

                        return (
                          <div key={part.id} className="rounded-lg border border-border bg-card px-4 py-2">
                            <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {{
                                validate_signal_config: 'Validating configuration...',
                                fetch_workspace_metrics_and_traits: 'Fetching workspace data...',
                                fetch_workspace_schema_for_signals: 'Fetching workspace data...',
                                create_custom_skill_for_signal: 'Creating analysis...',
                                list_available_skills: 'Loading available analyses...',
                                create_signal_from_config: 'Creating signal...',
                                fetch_and_show_filter_builder: 'Building filters...',
                                show_entity_selector: 'Loading entity options...',
                                show_description_suggestions: 'Generating suggestions...',
                                show_signal_summary: 'Preparing summary...',
                                show_skill_selector: 'Loading available skills...',
                              }[part.toolCall.tool_name] ?? `Processing...`}
                            </span>
                          </div>
                        );
                      }

                      // Assistant text or loading
                      if (part.role === 'assistant') {
                        if (part.isLoading) {
                          return (
                            <div key={part.id} className="w-full rounded-lg rounded-tl-sm border border-border bg-quaternary px-3.5 py-2.5 text-foreground">
                              <div className="flex gap-1 py-1">
                                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '0ms' }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '150ms' }} />
                                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          );
                        }
                        if (part.content) {
                          return (
                            <div key={part.id} className="w-full rounded-lg rounded-tl-sm border border-border bg-quaternary px-3.5 py-2.5 text-foreground">
                              <MarkdownRenderer classname="text-sm [&_p]:text-sm [&_p]:leading-[1.8] [&_p]:text-foreground [&_li]:text-sm [&_li]:leading-[1.8] [&_li]:text-muted-foreground [&_ul]:text-sm [&_ol]:text-sm [&_strong]:text-foreground">
                                {part.content}
                              </MarkdownRenderer>
                            </div>
                          );
                        }
                        return null;
                      }

                      return null;
                    })}
                  </div>
                </div>
              );
            })}

            {isStreaming && !isAssistantTyping && !(messages[messages.length - 1]?.role === 'tool' && (messages[messages.length - 1] as any)?.toolCall?.status === 'running') ? (
              <div className="flex gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-muted">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
                </div>
                <div className="rounded-lg rounded-tl-sm border border-border bg-quaternary px-3.5 py-2.5">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-border bg-background p-4">
          <div className="mx-auto max-w-4xl">
            {activeSignalName ? (
              <div className="mb-2 flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2">
                <p className="truncate text-xs text-muted-foreground">
                  Selected signal context: <span className="font-medium text-foreground">{activeSignalName}</span>
                </p>
                <button
                  type="button"
                  onClick={handleUseSelectedSignalContext}
                  className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
                >
                  Use in chat
                </button>
              </div>
            ) : null}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSend();
              }}
            >
              <div
                className={cn(
                  'relative rounded-lg border bg-background transition-all duration-200',
                  isFocused ? 'border-foreground/30' : 'border-border',
                )}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={handleKeyDown}
                  placeholder={activeSignalName
                    ? `Refine "${activeSignalName}" with ORA…`
                    : 'Type a signal name or respond to ORA…'}
                  disabled={isStreaming}
                  className="w-full bg-transparent px-5 py-4 pr-14 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isStreaming}
                  className={cn(
                    'absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[5px] transition-all',
                    input.trim() && !isStreaming
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'cursor-not-allowed bg-muted text-muted-foreground',
                  )}
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-2.5 flex items-center justify-between px-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      isStreaming ? 'animate-pulse bg-muted-foreground' : 'bg-foreground',
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isStreaming ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {isStreaming ? 'Thinking…' : 'AI Ready'}
                  </span>
                </div>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  <AtSign className="h-3.5 w-3.5" />
                  Mention accounts
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalChatBuilder;
